import { Loader2, RotateCcw, Sword, Target } from "lucide-react";
import { useState } from "react";
import { toast } from "@/components/toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { PartyAction, PartyPc } from "@/lib/core/schemas/party-view";
import type { Campaign } from "@/lib/core/types";
import logger from "@/lib/logger";

// --- Stubs ---

async function submitTurnAction(_opts: any) {
  // Stub
  await Promise.resolve(); // Satisfy useAwait
  logger.info("Turn submitted (stub)");
}

// --- End Stubs ---

type ClientRollResult = {
  id: string;
  actionName: string;
  log: string;
  timestamp: Date;
};

export function ActionsTab({
  campaign,
  character,
  mutateCampaign,
  _sendMessage,
}: {
  campaign: Campaign;
  character: PartyPc | null;
  mutateCampaign: () => Promise<any>;
  _sendMessage?: (message: any) => void;
}) {
  // Read-only: tapping a card toggles inline expansion of its (clamped)
  // description. Real invocation is deferred (Task 4 UAT).
  const [expandedActionId, setExpandedActionId] = useState<string | null>(null);
  const [isPassingTurn, setIsPassingTurn] = useState(false);
  const [history, setHistory] = useState<ClientRollResult[]>([]);

  // DEPRECATED: combatState is being refactored. Stubbing out for now.
  const combatState = (campaign as { combatState?: any }).combatState;
  const isCombatActive = false;
  const isMyTurn = true; // Allow actions for now since system is broken

  const combatants: {
    id: string;
    name: string;
    type: string;
    hpCurrent: number;
    hpMax: number;
  }[] = [];

  // Real resolved actions from the party://current projection.
  const actions: PartyAction[] = character?.availableActions ?? [];

  const handlePassTurn = async () => {
    setIsPassingTurn(true);
    try {
      await submitTurnAction({ campaignId: campaign.id });
      // if (sendMessage) {
      //   sendMessage({
      //     role: "user",
      //     parts: [{ type: "text", text: summary }],
      //   });
      // }
      toast({ type: "success", description: "Turn passed" });
      await mutateCampaign();
    } catch (error) {
      logger.error({ err: error }, "Pass turn failed");
      toast({ type: "error", description: "Failed to pass turn" });
    } finally {
      setIsPassingTurn(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <ScrollArea className="flex-1">
        <div className="mx-auto max-w-2xl space-y-4 p-4">
          {/* Turn Status */}
          {isCombatActive && (
            <Card
              className={`border-2 ${isMyTurn ? "border-primary bg-primary/5" : "border-muted"}`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Target
                    className={`h-4 w-4 ${isMyTurn ? "text-primary" : "text-muted-foreground"}`}
                  />
                  {isMyTurn ? "It's Your Turn!" : "Combat Active"}
                </CardTitle>
                <CardDescription>
                  {isMyTurn
                    ? "Select an action below and choose your target."
                    : `Waiting for ${combatants.find((c) => c.id === combatState.activeCombatantId)?.name || "next turn"}...`}
                </CardDescription>
              </CardHeader>
              {isMyTurn && (
                <CardContent className="pt-0">
                  <Button
                    className="w-full"
                    disabled={isPassingTurn}
                    onClick={handlePassTurn}
                    variant="secondary"
                  >
                    {isPassingTurn && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Pass Turn
                  </Button>
                </CardContent>
              )}
            </Card>
          )}
          {/* Available Actions */}
          <Card className="border-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-foreground">
                Available Actions
              </CardTitle>
              <CardDescription>
                Actions for {character ? character.name : "Character"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {actions.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  No actions available.
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {actions.map((action) => {
                    const cost =
                      Object.entries(action.costs)
                        .map(([k, v]) => `${v} ${k}`)
                        .join(", ") || "—";
                    // A resource can be both missing AND suppressed (the
                    // resolver populates the two arrays independently), so
                    // dedupe to avoid "missing action, action".
                    const reasons = Array.from(
                      new Set([
                        ...action.missingResources,
                        ...action.suppressedResources,
                      ])
                    );
                    const isExpanded = expandedActionId === action.id;

                    return (
                      <Button
                        // `whitespace-normal` + `min-w-0` override the shadcn
                        // Button's default `whitespace-nowrap`, which otherwise
                        // runs long names/descriptions off-screen on mobile.
                        // Kept clickable (not `disabled`) so unaffordable cards
                        // can still expand; affordability is shown via dimming.
                        className={`h-auto min-w-0 flex-col items-start gap-1 whitespace-normal p-3 text-left ${
                          action.affordable ? "" : "opacity-60"
                        }`}
                        key={action.id}
                        // Read-only: tap toggles inline expansion; real
                        // invocation is deferred (Task 4 UAT).
                        onClick={() =>
                          setExpandedActionId(isExpanded ? null : action.id)
                        }
                        type="button"
                        variant="outline"
                      >
                        <div className="flex w-full items-center justify-between gap-2">
                          <span className="min-w-0 flex-1 truncate font-semibold">
                            {action.name}
                          </span>
                          <Badge
                            className="shrink-0 text-[10px]"
                            variant="secondary"
                          >
                            Cost: {cost}
                          </Badge>
                        </div>
                        {action.description && (
                          <p
                            className={`text-muted-foreground text-xs ${
                              isExpanded ? "" : "line-clamp-2"
                            }`}
                          >
                            {action.description}
                          </p>
                        )}
                        {!action.affordable && reasons.length > 0 && (
                          <p className="text-destructive text-xs">
                            Unavailable: missing {reasons.join(", ")}
                          </p>
                        )}
                      </Button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
          {/* Roll History */}
          <Card className="border-border bg-card pb-16 md:pb-0">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base text-foreground">
                    Action History
                  </CardTitle>
                  <CardDescription>Recent combat results</CardDescription>
                </div>
                <Button
                  onClick={() => setHistory([])}
                  size="sm"
                  variant="ghost"
                >
                  <RotateCcw className="mr-1 h-4 w-4" />
                  Clear
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="py-4 text-center text-muted-foreground text-sm">
                  No actions taken yet this session.
                </p>
              ) : (
                <div className="space-y-2">
                  {history.map((item, index) => (
                    <div
                      className={`rounded-lg p-3 text-sm ${
                        index === 0
                          ? "border border-primary/30 bg-primary/10"
                          : "bg-secondary/50"
                      }`}
                      key={item.id}
                    >
                      <div className="mb-1 flex items-center justify-between font-semibold">
                        <span className="flex items-center gap-1">
                          <Sword className="h-3 w-3" /> {item.actionName}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {item.timestamp.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          })}
                        </span>
                      </div>
                      <p className="text-muted-foreground text-xs leading-relaxed">
                        {item.log}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </ScrollArea>
    </div>
  );
}
